import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pendingVerification, setPendingVerification] = useState(null);
    
    // Create ref at component top level
    const isFirstRender = useRef(true);
    // Add lastValidationTime ref to track when token was last validated
    const lastValidationTime = useRef(0);

    // Validate the token when the app loads
    const validateToken = async () => {
        try {
            // Check if it's too soon for another validation (prevent loop)
            const now = Date.now();
            if (now - lastValidationTime.current < 5000) {
                console.log('Skipping token validation, too soon since last check');
                return;
            }
            
            // Update the last validation time
            lastValidationTime.current = now;
            
            const token = localStorage.getItem('token');
            
            if (!token) {
                // Clear any existing user data if no token exists
                setUser(null);
                setLoading(false);
                return;
            }
            
            // Set the authorization header
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            // Add a timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            
            // Call the server to validate the token
            try {
                const response = await authService.getProfile();
                
                // Clear timeout since request completed
                clearTimeout(timeoutId);
                
                // If successful, update the user state with fresh data
                if (response) {
                    setUser(response);
                    localStorage.setItem('user', JSON.stringify(response));
                }
                
                setLoading(false);
                return response;
            } catch (error) {
                // Clear timeout
                clearTimeout(timeoutId);
                
                console.error('Token validation failed:', error?.response?.status || error.message || 'unknown error');
                
                // If unauthorized (401) or not found (404), the user has been deleted or password changed
                if (error?.response?.status === 401 || error?.response?.status === 404) {
                    // Clear local storage and log out
                    logout();
                }
                
                setLoading(false);
                throw error;
            }
        } catch (error) {
            console.error('Token validation outer error:', error);
            setLoading(false);
            throw error;
        }
    };

    useEffect(() => {
        // Only validate token once on initial load
        if (isFirstRender.current) {
            isFirstRender.current = false;
            validateToken();
        }
        
        // Set up an interceptor to handle 401 errors globally
        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                // Don't automatically redirect if on homepage
                const isHomePage = window.location.pathname === '/';
                
                if (error?.response?.status === 401 && user && !isHomePage) {
                    // If we get a 401 error and we have a user, log them out (except on homepage)
                    logout();
                    window.location.href = '/signin';
                }
                return Promise.reject(error);
            }
        );
        
        // Remove the focus event handler from here as it's redundant with the one in App.js
        // This was causing duplicate validation calls
        
        // Clean up
        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, [user]);

    const login = async (identifier, password) => {
        try {
            const response = await authService.login(identifier, password);
            const { token, user } = response;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            setUser(user);
            return user;
        } catch (error) {
            if (error.response?.data?.requiresVerification) {
                // Store the email to complete verification
                localStorage.setItem('pendingVerification', error.response.data.email);
                setPendingVerification(error.response.data.email);
            }
            throw error;
        }
    };

    const signup = async (name, email, password, role = 'user', additionalData = {}) => {
        try {
            const response = await authService.signup(name, email, password, role, additionalData);
            
            // Set pending verification if response indicates it's required
            if (response.requires_verification) {
                localStorage.setItem('pendingVerification', email);
                setPendingVerification(email);
            }
            
            return response;
        } catch (error) {
            // Check if the error is related to disposable email
            if (error.response?.data?.error?.includes('Disposable email')) {
                throw new Error('Please use a permanent email address. Temporary or disposable email addresses are not allowed.');
            }
            throw error;
        }
    };

    const verifyEmail = async (email, code) => {
        try {
            const response = await authService.verifyEmail(email, code);
            const { token, user } = response;
            
            // Auto login after successful verification
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.removeItem('pendingVerification');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            setUser(user);
            setPendingVerification(null);
            return user;
        } catch (error) {
            throw error;
        }
    };

    const resendVerification = async (email) => {
        return await authService.resendVerification(email);
    };

    const verifyToken = async (token) => {
        try {
            const response = await authService.verifyToken(token);
            const { token: jwtToken, user } = response;
            
            // Auto login after successful verification
            localStorage.setItem('token', jwtToken);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.removeItem('pendingVerification');
            axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
            
            setUser(user);
            setPendingVerification(null);
            return user;
        } catch (error) {
            throw error;
        }
    };

    const logout = () => {
        // Remove auth-related items
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('pendingVerification');
        
        // Don't remove cookie consent settings when logging out
        // This prevents the cookie consent banner from reappearing after logout
        // localStorage.removeItem('cookieConsent');
        // localStorage.removeItem('cookieSettings');
        
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setPendingVerification(null);
    };

    const isManager = () => {
        return user && (user.role_name === 'manager' || user.role_name === 'admin' || user.role === 'manager' || user.role === 'admin');
    };

    const isAdmin = () => {
        return user && (user.role_name === 'admin' || user.role === 'admin');
    };

    const isLegalProfessional = () => {
        return user && (user.role_name === 'legal_professional' || user.role === 'legal_professional');
    };

    const clearPendingVerification = () => {
        localStorage.removeItem('pendingVerification');
        setPendingVerification(null);
    };

    // Add refreshUser method to update user data after profile changes
    const refreshUser = async () => {
        try {
            const userData = await validateToken();
            return userData;
        } catch (error) {
            console.error('Failed to refresh user data:', error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            loading, 
            login, 
            logout, 
            signup,
            verifyEmail,
            resendVerification,
            verifyToken,
            pendingVerification,
            clearPendingVerification,
            isManager, 
            isAdmin,
            isLegalProfessional,
            validateToken,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext; 